// updateModelPrices() in variant-radios.js calls these two as bare globals. They are
// declared in configurator-product-form.js, which is the other configurator's add-on and
// layout script — pulling that whole file in just for two formatters would bring its DOM
// handling (.configurator-bottom-wrapper, the mobile sticky header) onto templates that
// have neither. Assigned rather than declared, so configurator-product-form.js still
// overrides them where it loads and that template runs exactly the code it did before.
window.formatPrice =
  window.formatPrice ||
  function (price) {
    var formatted = Shopify.formatMoney(price, '${{amount}}');
    if (window.currencyCodeEnabled && Shopify.currency && Shopify.currency.active) {
      formatted += ' ' + Shopify.currency.active;
    }
    return formatted;
  };

window.formatWithCurrency =
  window.formatWithCurrency ||
  function (amount) {
    return Shopify.formatMoney(amount, '${{amount}}') + ' ' + Shopify.currency.active;
  };

// The configurator swaps configs by lifting the sibling product's <media-gallery>
// out of that product's own page, and by writing that product's URL into the address
// bar. Both assume the sibling renders the same template as the page doing the
// swapping — true when every config product is assigned one configurator template,
// false for a template used as a ?view= alternate, where the sibling comes back in
// its assigned template's markup instead. A section can publish its own view name on
// [data-gallery-view] to keep the swap on one template; without that attribute this
// returns the URL untouched, which is the original configurator's behaviour.
// The configurator rewrites document.title as the shopper switches configs. The suffix
// used to be the literal "– Rezy Design Canada", which put Canadian branding on every
// US tab. window.shopName is published by theme.liquid, so each store names itself; the
// separator is lifted out of whatever Shopify actually rendered into <title>, because
// the two stores do not use the same one (an en dash on CA, a pipe on US).
let cachedTitleSuffix = null;
function documentTitleSuffix() {
  if (cachedTitleSuffix !== null) return cachedTitleSuffix;
  const name = window.shopName;
  if (!name) {
    cachedTitleSuffix = '';
    return cachedTitleSuffix;
  }
  const at = document.title.lastIndexOf(name);
  // Not in the rendered title (a page that suppresses it): fall back to a plain dash
  // rather than dropping the branding entirely.
  if (at < 1) {
    cachedTitleSuffix = ` – ${name}`;
    return cachedTitleSuffix;
  }
  const separator = document.title.slice(0, at).match(/[\s–—|-]+$/);
  cachedTitleSuffix = (separator ? separator[0] : ' – ') + name;
  return cachedTitleSuffix;
}

function withConfiguratorView(url) {
  const view = document.querySelector('[data-gallery-view]')?.dataset.galleryView;
  if (!view) return url;
  return `${url}${url.includes('?') ? '&' : '?'}view=${encodeURIComponent(view)}`;
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.currentProductId = this.getCurrentProductId(true);
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback() {
    this.fetchCheckoutLabels();
  }

  getCurrentProductId(refresh = false) {
    if (!refresh) return this.currentProductId;
    // Only the visible form counts, same rule as getSelectedSectional(). There is one
    // <form> per seat count and each is its own radio group, so a form that has been
    // hidden keeps the radio it had checked. Matching across all of them returned
    // whichever form came first in the DOM, so onRadioChange()'s updateCurrentProductId()
    // reset currentProductId to the previous config immediately after showProduct() had
    // set the new one — leaving the id and the cached variant data pointing at different
    // products, and the price stale on the next change.
    let checkedSectional = this.querySelector('.sectionals form:not(.hidden) input:checked');
    if (checkedSectional) {
      if (checkedSectional.dataset.productTitle) {
        document.title = `${checkedSectional.dataset.productTitle}${documentTitleSuffix()}`;
      }
      return checkedSectional.dataset.productId;
    }
    return undefined;
  }

  updateCurrentProductId() {
    const currentProductId = this.getCurrentProductId(true);
    if (currentProductId !== undefined) {
      this.currentProductId = currentProductId;
    }
  }

  async fetchCheckoutLabels() {
    try {
      this.updateOptions();
      this.updateSelectedValue();
      this.updateMasterId();

      if (this.currentVariant) {
        if (!this.currentVariant.available) {
          this.toggleAddButton(true, window.variantStrings.soldOut, true);
        } else {
          this.toggleAddButton(false, '', true);
        }

        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          detail: {
            variant: this.currentVariant,
            source: this,
          },
          bubbles: true,
          composed: true,
        });
      }
    } catch (error) {
      console.error('Error fetching checkout labels:', error);
    }
  }

  onVariantChange(event) {
    if (!window.isConfiguratorSectional) {
      return;
    }
    this.updateOptions();
    this.updateSelectedValue();
    this.updateMasterId();
    this.updateDimensions();
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updatePrice();

    if (this.currentVariant) {
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        detail: {
          variant: this.currentVariant,
          source: this,
        },
        bubbles: true,
        composed: true,
      });

      this.filterMedia();
      this.updateMedia();
      const layoutSection = this.querySelector('#main-product.content-section');
      const isLayoutChange = layoutSection?.contains(event?.target) || false;
      if (!isLayoutChange) {
        this.updateURL();
      }
      this.updateVariantInput();
      this.updateShareUrl();

      if (!this.currentVariant.available) {
        this.toggleAddButton(true, window.variantStrings.soldOut, true);
        this.setUnavailable('soldOut');
      } else {
        const price = document.getElementById(`price-${this.dataset.section}`);
        if (price && price.classList.contains('visibility-hidden')) {
          price.classList.remove('visibility-hidden');
        }

        this.toggleAddButton(false, '', true);
        this.renderProductInfo();
      }
    } else {
      this.toggleAddButton(true, window.variantStrings.unavailable, true);
      this.setUnavailable();
    }
  }

  updatePrice() {
    const priceContainerElement = this.querySelector('.price__container');
    if (!priceContainerElement) return;

    const salePriceElement = priceContainerElement.querySelector('.price__sale .price-item--sale');
    const regularPriceElement = priceContainerElement.querySelector('.price__regular .price-item--regular');

    if (!salePriceElement || !regularPriceElement) return;

    const salePriceInitial = parseFloat(priceContainerElement.dataset.price);
    const regularPriceInitial = parseFloat(priceContainerElement.dataset.comparedAt);

    const updatedSalePrice = salePriceInitial + parseFloat(priceContainerElement.dataset.totalPrice);
    const updatedComparePrice = regularPriceInitial + parseFloat(priceContainerElement.dataset.totalCompare);

    salePriceElement.dataset.price = updatedSalePrice;
    regularPriceElement.dataset.comparedAt = updatedComparePrice;

    salePriceElement.innerHTML = Shopify.formatMoney(updatedSalePrice, '${{amount}} ' + Shopify.currency.active);

    regularPriceElement.innerHTML = Shopify.formatMoney(updatedComparePrice, '${{amount}} ' + Shopify.currency.active);
  }

  updateDimensions() {
    let dimensionsWrapper = document.querySelector('#ProductAccordion-dimensions');
    let listOfDimensions = dimensionsWrapper.querySelectorAll('.dimensions__wrapper');
    const currentId = this.currentVariant && this.currentVariant.id;

    listOfDimensions.forEach((dimension) => {
      if (!dimension.classList.contains('hidden')) {
        dimension.classList.add('hidden');
      }
      if (!currentId) return;

      // A wrapper covers every variant sharing the same dimensions: data-variant-ids
      // is a "[id][id]…" list built by popup-specifications*.liquid. data-variant-id
      // is the legacy one-wrapper-per-variant form, kept as a fallback.
      const coveredIds = dimension.dataset.variantIds;
      const matches = coveredIds
        ? coveredIds.indexOf('[' + currentId + ']') !== -1
        : Number(dimension.dataset.variantId) === currentId;

      if (matches) {
        dimension.classList.remove('hidden');
      }
    });
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    const selectedVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });

    if (!selectedVariant) return;
    this.currentVariant = selectedVariant;
  }

  filterMedia() {
    if (!this.currentVariant.featured_media || !this.currentVariant.featured_media.alt) return false;

    document.querySelectorAll('[thumbnail-color]').forEach((item) => {
      item.classList.add('slide-hide');
      item.classList.remove('slide-show');
    });

    var selected_variant = this.currentVariant.featured_media.alt;
    var selected_attribute = '[thumbnail-color="' + selected_variant + '"]';

    document.querySelectorAll(selected_attribute).forEach((item) => {
      item.classList.remove('slide-hide');
      item.classList.add('slide-show');
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGallery = document.getElementById(`MediaGallery-${this.currentProductId}`);

    if (mediaGallery) {
      // Remove slide-show class from ALL media items, not just the active one
      mediaGallery.querySelectorAll('[data-media-id].product__media-item--variant').forEach((element) => {
        element.classList.remove('slide-show');
      });

      mediaGallery.setActiveMedia(`${this.currentVariant.featured_media.id}`, true);

      const newActiveMedia = mediaGallery.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
      if (newActiveMedia) {
        newActiveMedia.classList.add('slide-show');
      }
    }

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (modalContent) {
      const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
      modalContent.prepend(newMediaModal);
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateurl === 'false') return;
    window.history.replaceState({}, '', withConfiguratorView(`${this.dataset.url}?variant=${this.currentVariant.id}`));
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const currentVariant = this.currentVariant;

    if (currentVariant && this.getVariantData()) {
      this.updatePriceFromVariant(currentVariant);
      return;
    }

    this.fetchAndUpdateProductInfo();
  }

  updatePriceFromVariant(variant) {
    const priceElement = document.getElementById(`price-${this.dataset.section}`);
    if (!priceElement) return;

    // Update data-price and data-compared-at attributes on the .price__container element
    const priceContainer = priceElement.querySelector('.price__container');
    if (!priceContainer) return;
    priceContainer.setAttribute('data-price', variant.price);
    priceContainer.setAttribute('data-compared-at', variant.compare_at_price || 0);

    const priceElements = this.getPriceElements(priceContainer);
    this.updatePriceDisplay(priceElements, priceContainer);

    this.toggleAddButton(!variant.available, window.variantStrings.soldOut);
  }

  getPriceElements(priceContainer) {
    return {
      regularPriceElement: priceContainer.querySelector('.price__regular .price-item--regular'),
      salePriceElement: priceContainer.querySelector('.price__sale .price-item--sale'),
      compareAtPriceElement: priceContainer.querySelector('.price__sale .price-item--compare'),
    };
  }

  updatePriceDisplay(priceElements, priceContainer) {
    const { regularPriceElement, salePriceElement, compareAtPriceElement } = priceElements;
    const salePriceInitial = parseFloat(priceContainer.dataset.price);
    const regularPriceInitial = parseFloat(priceContainer.dataset.comparedAt);
    const updatedSalePrice = salePriceInitial + parseFloat(priceContainer.dataset.totalPrice);
    const updatedComparePrice = regularPriceInitial + parseFloat(priceContainer.dataset.totalCompare);
    const isOnSale = updatedComparePrice > updatedSalePrice;

    if (isOnSale) {
      this.updateSalePrice(
        updatedSalePrice,
        updatedComparePrice,
        regularPriceElement,
        salePriceElement,
        compareAtPriceElement,
      );
      priceContainer.classList.add('price--on-sale');
    } else {
      this.updateRegularPrice(updatedSalePrice, regularPriceElement, salePriceElement, compareAtPriceElement);
      priceContainer.classList.remove('price--on-sale');
    }

    // price.liquid also stamps price--on-sale on the outer .price at render time. Only
    // .price__container was being toggled here, so the stale class kept .price__sale
    // displayed after switching to a variant that isn't discounted.
    const priceRoot = priceContainer.closest('.price');
    if (priceRoot) priceRoot.classList.toggle('price--on-sale', isOnSale);

    // price.liquid inline-hides whichever block the first-rendered variant didn't need.
    // Inline styles beat the class-based rules above, so the wrappers have to be toggled
    // explicitly or a variant that switches sale state renders an empty price.
    // Cleared rather than set to a value, so each surface's own CSS decides the layout.
    const regularWrapper = priceContainer.querySelector('.price__regular');
    const saleWrapper = priceContainer.querySelector('.price__sale');
    if (regularWrapper) regularWrapper.style.display = isOnSale ? 'none' : '';
    if (saleWrapper) saleWrapper.style.display = isOnSale ? '' : 'none';

    if (typeof window.syncConfiguratorPromo === 'function') {
      window.syncConfiguratorPromo(updatedSalePrice, updatedComparePrice);
    }

    priceContainer.classList.remove('visibility-hidden');

    void priceContainer.offsetWidth;
  }

  updateSalePrice(updatedSalePrice, updatedComparePrice, regularPriceElement, salePriceElement, compareAtPriceElement) {
    if (regularPriceElement) regularPriceElement.style.display = 'none';

    if (salePriceElement) {
      salePriceElement.textContent = Shopify.formatMoney(updatedSalePrice, '${{amount}} ' + Shopify.currency.active);
      // updateRegularPrice inline-hides this; clear it or a variant that goes back on
      // sale renders blank.
      salePriceElement.style.display = '';
    }

    if (compareAtPriceElement) {
      compareAtPriceElement.textContent = Shopify.formatMoney(
        updatedComparePrice,
        '${{amount}} ' + Shopify.currency.active,
      );
      compareAtPriceElement.style.display = '';
    }
  }

  updateRegularPrice(updatedSalePrice, regularPriceElement, salePriceElement, compareAtPriceElement) {
    if (regularPriceElement) {
      regularPriceElement.textContent = Shopify.formatMoney(updatedSalePrice, '${{amount}} ' + Shopify.currency.active);
      regularPriceElement.style.display = 'block';
    }

    if (salePriceElement) salePriceElement.style.display = 'none';
    if (compareAtPriceElement) compareAtPriceElement.style.display = 'none';
  }

  fetchAndUpdateProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(id);
        const source = html.getElementById(id);

        if (source && destination) destination.innerHTML = source.innerHTML;

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('visibility-hidden');
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButtons = productForm.querySelectorAll(
      '[name="add"][data-button-type="add"], [name="add"][data-button-type="next"]',
    );

    addButtons.forEach((addButton) => {
      const addButtonText = addButton.querySelector('span');
      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        const isNextButton = addButton.getAttribute('data-button-type') === 'next';
        if (isNextButton) {
          addButtonText.textContent = window.variantStrings.nextStep;
        } else {
          addButtonText.textContent = window.variantStrings.addToCart;
        }
      }
    });

    toggleAddOnsTabs(!disable);

    if (!modifyClass) return;
  }

  setUnavailable(status = 'unavailable') {
    const formButtons = document.getElementById(`product-form-${this.dataset.section}`);
    if (!formButtons) return;
    const price = document.getElementById(`price-${this.dataset.section}`);
    this.toggleAddButton(true, window.variantStrings[status], true);

    if (price) price.classList.add('visibility-hidden');
  }

  getVariantData(productId = this.currentProductId) {
    if (productId == undefined) {
      // Not from variant-radios
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    }
    if (productId == this.currentProductId) {
      this.variantData =
        this.variantData || JSON.parse(this.querySelector(`#product-${productId}-variants`).textContent);
    } else {
      this.variantData = JSON.parse(this.querySelector(`#product-${productId}-variants`).textContent);
    }
    return this.variantData;
  }

  updateSelectedValue() {
    if (this.currentVariant && this.currentVariant.option1) {
      const valueOption1 = this.currentVariant.option1;
      const mainSlider = document.getElementsByClassName('main-slider')[0];
      const selectedColorElement = this.getElementsByClassName('selected-value')[0];
      if (valueOption1 && selectedColorElement) {
        selectedColorElement.innerHTML = valueOption1;
        selectedColorElement.setAttribute('data-selected-variant-id', this.currentVariant.id);
      }
      if (valueOption1 && mainSlider) {
        const colorClasses = ['white', 'cream', 'charcoal', 'grey-blue', 'blue'];
        colorClasses.forEach((color) => mainSlider.classList.remove(color));
        const colorClass = valueOption1.toLowerCase().replace(/ /g, '-');
        mainSlider.classList.add(colorClass);
      }
    }
  }
}

customElements.define('variant-selects', VariantSelects);